import React, { useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Box, Plane, Cylinder, Sphere } from '@react-three/drei';
import * as THREE from 'three';

interface Agent3DProps {
  position: [number, number, number];
  emoji: string;
  name: string;
  rpgClass: string;
  level: number;
  color: string;
  isSelected: boolean;
  onClick: () => void;
}

// Simple 3D Character Avatar (placeholder until GLB models available)
function AgentAvatar({ position, emoji, name, rpgClass, level, color, isSelected, onClick }: Agent3DProps) {
  const meshRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      // Gentle floating animation
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.05;
      // Selection glow effect
      if (isSelected) {
        meshRef.current.rotation.y += 0.01;
      }
    }
  });

  return (
    <group
      ref={meshRef}
      position={position}
      onClick={onClick}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
      scale={hovered || isSelected ? 1.1 : 1}
    >
      {/* Character body */}
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.3, 0.3, 1, 16]} />
        <meshStandardMaterial color={color} />
      </mesh>

      {/* Character head */}
      <mesh position={[0, 1.2, 0]}>
        <sphereGeometry args={[0.35, 16, 16]} />
        <meshStandardMaterial color={color} emissive={isSelected ? color : '#000000'} emissiveIntensity={isSelected ? 0.3 : 0} />
      </mesh>

      {/* Selection ring */}
      {isSelected && (
        <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.6, 0.7, 32]} />
          <meshBasicMaterial color="#D4AF37" transparent opacity={0.8} />
        </mesh>
      )}

      {/* Name tag */}
      <group position={[0, 1.8, 0]}>
        <mesh>
          <planeGeometry args={[1.2, 0.3]} />
          <meshBasicMaterial color="rgba(0,0,0,0.8)" transparent />
        </mesh>
      </group>
    </group>
  );
}

// Office Desk
function OfficeDesk({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      {/* Desk top */}
      <mesh position={[0, 0.8, 0]}>
        <boxGeometry args={[2, 0.1, 1]} />
        <meshStandardMaterial color="#3d2817" />
      </mesh>
      {/* Desk legs */}
      <mesh position={[-0.9, 0.4, -0.4]}>
        <cylinderGeometry args={[0.05, 0.05, 0.8]} />
        <meshStandardMaterial color="#2a1b0f" />
      </mesh>
      <mesh position={[0.9, 0.4, -0.4]}>
        <cylinderGeometry args={[0.05, 0.05, 0.8]} />
        <meshStandardMaterial color="#2a1b0f" />
      </mesh>
      <mesh position={[-0.9, 0.4, 0.4]}>
        <cylinderGeometry args={[0.05, 0.05, 0.8]} />
        <meshStandardMaterial color="#2a1b0f" />
      </mesh>
      <mesh position={[0.9, 0.4, 0.4]}>
        <cylinderGeometry args={[0.05, 0.05, 0.8]} />
        <meshStandardMaterial color="#2a1b0f" />
      </mesh>
      {/* Computer monitor */}
      <mesh position={[0, 1.3, -0.3]}>
        <boxGeometry args={[0.6, 0.4, 0.05]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
    </group>
  );
}

// Office Floor
function OfficeFloor() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[20, 20]} />
      <meshStandardMaterial color="#2a2a3a" />
    </mesh>
  );
}

// Lighting Setup
function Lighting() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
      <pointLight position={[-5, 5, -5]} intensity={0.5} color="#D4AF37" />
      <pointLight position={[5, 5, 5]} intensity={0.5} color="#4a90e2" />
    </>
  );
}

interface Agent3DData {
  agentId: string;
  name: string;
  emoji: string;
  rpgClass: string;
  level: number;
  color: string;
  position: [number, number, number];
}

const AGENTS_3D: Agent3DData[] = [
  {
    agentId: 'peter',
    name: 'Peter',
    emoji: '⚡',
    rpgClass: 'Commander',
    level: 8,
    color: '#D4AF37',
    position: [0, 0, 0],
  },
  {
    agentId: 'james',
    name: 'James',
    emoji: '👑',
    rpgClass: 'Knight',
    level: 7,
    color: '#4ecdc4',
    position: [-4, 0, 2],
  },
  {
    agentId: 'john',
    name: 'John',
    emoji: '📊',
    rpgClass: 'Oracle',
    level: 6,
    color: '#dda0dd',
    position: [4, 0, 2],
  },
];

interface AgentScene3DProps {
  selectedAgentId: string;
  onSelectAgent: (agentId: string) => void;
}

export default function AgentScene3D({ selectedAgentId, onSelectAgent }: AgentScene3DProps) {
  return (
    <div style={{ width: '100%', height: '100vh', background: '#0a0a1a' }}>
      <Canvas
        camera={{ position: [0, 5, 8], fov: 60 }}
        shadows
        gl={{ antialias: true, alpha: true }}
      >
        <Lighting />
        <OfficeFloor />
        
        {/* Office Desks */}
        <OfficeDesk position={[0, 0, -1]} />
        <OfficeDesk position={[-4, 0, 1]} />
        <OfficeDesk position={[4, 0, 1]} />
        
        {/* Agent Avatars */}
        {AGENTS_3D.map((agent) => (
          <AgentAvatar
            key={agent.agentId}
            position={agent.position}
            emoji={agent.emoji}
            name={agent.name}
            rpgClass={agent.rpgClass}
            level={agent.level}
            color={agent.color}
            isSelected={selectedAgentId === agent.agentId}
            onClick={() => onSelectAgent(agent.agentId)}
          />
        ))}
        
        {/* Camera Controls */}
        <OrbitControls
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={3}
          maxDistance={15}
          maxPolarAngle={Math.PI / 2 - 0.1}
        />
      </Canvas>
    </div>
  );
}
